import { Inject, inject, Injectable, Injector } from '@angular/core';
import * as L from 'leaflet';
import { PROJECT_PROVIDER } from '../../core/tokens';
import { ProjectProvider } from '../../core/interfaces';
import { LayerDetails } from '../types';
import { LayerService } from '../services/layer.service';
import { DrawingService } from './drawing.service';
import { PointRenderingService } from './point-rendering.service';
import { PolygonRenderingService } from './polygon-rendering.service';

/**
 * Service für Layer-Reihenfolge (Z-Index-Management).
 * Extrahiert aus MapComponent (Plan 15 Folgearbeit Task 20).
 */
@Injectable({ providedIn: 'root' })
export class LayerOrderingService {

  private map!: L.Map;
  private injector = inject(Injector);
  private _drawingService?: DrawingService;
  /** Lazy-Injection um zirkuläre Abhängigkeit DrawingService ↔ LayerOrderingService zu vermeiden */
  private get drawingService(): DrawingService {
    if (!this._drawingService) {
      this._drawingService = this.injector.get(DrawingService);
    }
    return this._drawingService;
  }

  constructor(
    private ls: LayerService,
    @Inject(PROJECT_PROVIDER) private ps: ProjectProvider,
    private pointRenderingService: PointRenderingService,
    private polygonRenderingService: PolygonRenderingService,
  ) {}

  init(map: L.Map): void {
    this.map = map;
  }

  destroy(): void {
    this.map = undefined!;
  }

  async onLayerOrderChanged(order?: string[]): Promise<void> {
    if (!this.map || !this.ls.mapDef()) return;

    let layerOrder = order ?? await this.ps.getProjectValue('layerOrder') as string[] | null;

    // Alle aktuellen Layer-Keys sammeln
    const currentLayerKeys = new Set<string>();
    if (this.pointRenderingService.pointsLayer) currentLayerKeys.add('__points__');
    if (this.polygonRenderingService.polygonsLayer) currentLayerKeys.add('__polygons__');
    for (const layerId of this.drawingService.getZeichnungslayerIds()) {
      currentLayerKeys.add(layerId);
    }
    const layers = this.ls.mapDef()?.layers ?? [];
    for (const ld of layers) {
      if (ld.type === 'gpkg' && ld.layers && ld.layers.length > 0) {
        for (const sublayer of ld.layers) {
          if (sublayer.enabled) {
            currentLayerKeys.add(`layer:${ld.name}_${sublayer.name}`);
          }
        }
      } else if (ld.type === 'wms' && ld.layers && ld.layers.some(l => l.enabled)) {
        for (const sublayer of ld.layers) {
          if (sublayer.enabled) {
            currentLayerKeys.add(`layer:${ld.name}_${sublayer.title || sublayer.name}`);
          }
        }
      } else {
        currentLayerKeys.add(`layer:${ld.name}`);
      }
    }

    // Falls keine Reihenfolge gespeichert, Standard-Reihenfolge generieren
    if (!layerOrder || layerOrder.length === 0) {
      layerOrder = [];
      if (this.pointRenderingService.pointsLayer) layerOrder.push('__points__');
      if (this.polygonRenderingService.polygonsLayer) layerOrder.push('__polygons__');
      for (const layerId of this.drawingService.getZeichnungslayerIds()) {
        layerOrder.push(layerId);
      }
      for (let i = layers.length - 1; i >= 0; i--) {
        const ld = layers[i];
        if (ld.type === 'gpkg' && ld.layers && ld.layers.length > 0) {
          for (const sublayer of ld.layers) {
            if (sublayer.enabled) {
              layerOrder.push(`layer:${ld.name}_${sublayer.name}`);
            }
          }
        } else if (ld.type === 'wms' && ld.layers && ld.layers.some(l => l.enabled)) {
          for (const sublayer of ld.layers) {
            if (sublayer.enabled) {
              layerOrder.push(`layer:${ld.name}_${sublayer.title || sublayer.name}`);
            }
          }
        } else {
          layerOrder.push(`layer:${ld.name}`);
        }
      }
    } else {
      // Neue Layer hinzufügen die noch nicht in der Order sind
      const existingKeys = new Set(layerOrder);
      const newKeys: string[] = [];
      currentLayerKeys.forEach(key => {
        if (!existingKeys.has(key)) {
          newKeys.push(key);
        }
      });

      let orderChanged = false;
      if (newKeys.length > 0) {
        // Neue Layer nach __points__ und Zeichnungslayer einfügen, nicht davor
        const firstNormalIdx = layerOrder.findIndex(k => k.startsWith('layer:'));
        if (firstNormalIdx >= 0) {
          layerOrder = [
            ...layerOrder.slice(0, firstNormalIdx),
            ...newKeys,
            ...layerOrder.slice(firstNormalIdx)
          ];
        } else {
          layerOrder = [...layerOrder, ...newKeys];
        }
        orderChanged = true;
      }

      const filteredOrder = layerOrder.filter(key => currentLayerKeys.has(key));
      if (filteredOrder.length !== layerOrder.length) {
        orderChanged = true;
      }
      layerOrder = filteredOrder;

      if (orderChanged) {
        this.ps.setProjectValue('layerOrder', layerOrder).then(() => {
          this.ls.zeichnungslayerChanged.emit();
        });
      }
    }

    if (layerOrder.length === 0) return;

    const baseZIndex = 450;
    const maxZIndex = baseZIndex + (layerOrder.length + 5) * 10;

    layerOrder.forEach((key, index) => {
      const zIndex = baseZIndex + (layerOrder!.length - index) * 10;

      if (key.startsWith('layer:')) {
        const layerName = key.replace('layer:', '');
        this.setLayerZIndex(layerName, zIndex);
      } else if (key === '__points__') {
        this.setPointsLayerZIndex(zIndex);
      } else if (key === '__polygons__') {
        this.setPolygonsLayerZIndex(zIndex);
      } else if (this.drawingService.hasZeichnungslayer(key)) {
        this.drawingService.setZeichnungslayerByIdZIndex(key, zIndex);
      }
    });

    // Fallback: Wenn Punkte/Polygone nicht in layerOrder sind, oben anzeigen
    if (!layerOrder.includes('__points__')) {
      this.setPointsLayerZIndex(maxZIndex + 30);
    }
    if (!layerOrder.includes('__polygons__')) {
      this.setPolygonsLayerZIndex(maxZIndex + 20);
    }
  }

  private setPointsLayerZIndex(zIndex: number): void {
    const pane = this.map.getPane('layer-Punkte');
    if (pane) {
      pane.style.zIndex = String(zIndex);
    }
  }

  private setPolygonsLayerZIndex(zIndex: number): void {
    const pane = this.map.getPane('layer-Flaechen');
    if (pane) {
      pane.style.zIndex = String(zIndex);
    }
  }

  private setLayerZIndex(layerName: string, zIndex: number): void {
    // GPKG-Sublayer: z.B. "datei.gpkg_sublayer"
    const gpkgMatch = layerName.match(/^(.+\.gpkg)_(.+)$/i);
    if (gpkgMatch) {
      const sublayerName = gpkgMatch[2];
      const subPaneName = `layer-${sublayerName.replace(/[^a-zA-Z0-9]/g, '_')}`;
      const subPane = this.map.getPane(subPaneName);
      if (subPane) {
        subPane.style.zIndex = String(zIndex);
      }
      return;
    }

    // WMS-Sublayer: z.B. "MV Naturraumkarte_Landschaftseinheiten"
    const wmsLd = this.ls.mapDef()?.layers.find((l: LayerDetails) =>
      l.type === 'wms' && l.layers?.some(sub =>
        layerName === `${l.name}_${sub.title || sub.name}`
      )
    );
    if (wmsLd) {
      const sublayer = wmsLd.layers!.find(sub =>
        layerName === `${wmsLd.name}_${sub.title || sub.name}`
      );
      if (sublayer) {
        const subName = sublayer.title || sublayer.name;
        const subPaneName = `layer-${subName.replace(/[^a-zA-Z0-9]/g, '_')}`;
        const subPane = this.map.getPane(subPaneName);
        if (subPane) {
          subPane.style.zIndex = String(zIndex);
        }
      }
      return;
    }

    // Normaler Layer
    const paneName = `layer-${layerName.replace(/[^a-zA-Z0-9]/g, '_')}`;
    const pane = this.map.getPane(paneName);
    if (pane) {
      pane.style.zIndex = String(zIndex);
    }
  }
}
